/* eslint no-template-curly-in-string: 0 */
import { Document, Packer, SectionType, Paragraph, convertInchesToTwip } from 'docx';

import {
    createBulletList,
    createHeading,
    createPageHeader,
    createParagraph,
    createStyles,
    createTitlePage,
    defaultTheme,
    generateDocumentFromTemplate,
    Theme,
    Template,
    createMultiEventTable,
    createTableParagraph,
    createStylizedSubHeading,
} from './generate-docx.utils';

import { smartFormat } from '@/impact-brief/utils/formatting.utils';

const HEADING_1 = 'HEADING_1' as const;
const HEADING_2 = 'HEADING_2' as const;

interface TimeToCompleteMetrics {
    timeToValueFirstComparisonRange: string;
    timeToValueSecondComparisonRange: string;
    timeToValuePercentComparison: number;
    sentiment: 'positive' | 'negative' | 'neutral';
    name?: string;
}

interface OnboardingMetrics {
    onboardingPercentChange: number;
    percentageCompleteFirstComparisonRange: number;
    completedCountBefore: number;
    newCountBefore: number;
    percentageCompleteSecondComparisonRange: number;
    completedCountAfter: number;
    newCountAfter: number;
    type: 'users' | 'accounts';
    sentiment: 'positive' | 'negative' | 'neutral';
    name?: string;
}

interface InfluencedMetrics {
    count: number;
    type: 'visitor' | 'account';
}
interface ViewCounts {
    sumViewCount: number;
    totalAccountsOrVisitors: number;
    type: 'visitor' | 'account';
}

interface RetentionMetrics {
    completedOnboardingRetentionPercent?: number;
    incompleteOnboardingRetentionPercent?: number;
    onboardingRetentionPercentGoal?: number;
    retentionTotalUsersCount?: number;
    retentionEstimatedUsersCount?: number;
    sentiment?: 'positive' | 'negative' | 'neutral';
    onboardingLength?: number;
    onboardingRetentionPercentDifference?: number;
    name?: string;
}

interface EventMetrics {
    appName: string;
    onboardingSuccessEventDescription: string;
    keyOnboardingEvent: string;
    sentiment?: 'positive' | 'negative' | 'neutral';
    timeToCompleteMetrics: TimeToCompleteMetrics;
    onboardingMetrics: OnboardingMetrics;
    retentionMetrics: RetentionMetrics;
}

interface OnboardingEventMetrics {
    segmentName: string;
    startFirstPeriod: string;
    endFirstPeriod: string;
    startSecondPeriod: string;
    endSecondPeriod: string;
    totalNewVisitorsFirstComparisonRange: string;
    totalNewAccountsFirstComparisonRange: string;
    metricType: 'accounts' | 'users';
    perEventMetrics: Record<string, EventMetrics>;
}

interface DocumentContent {
    companyName: string;
    onboardingEventMetrics: OnboardingEventMetrics;
    expectedDaysToOnboard: number;
    numberOfGuidesWithGoals: number;
    feedbackResponses: string;
    numberOfGuides: number;
    guides: Array<{ name: string }>;
    engineeringHoursSaved: number;
    influencedMetrics: InfluencedMetrics;
    viewCounts: ViewCounts;
    startFirstPeriod: string;
    endFirstPeriod: string;
    startSecondPeriod: string;
    endSecondPeriod: string;
    dayCount: number;
    showAscendingSuggestedActions: boolean;
}

interface ImpactBriefData {
    title: string;
    subtitle?: string;
    startPeriod: string;
    endPeriod: string;
    content?: DocumentContent;
    summary?: string;
    guides?: Array<{ name: string }>;
    engineeringHoursSaved?: number;
}

export const impactBriefTheme: Theme = {
    name: 'ImpactBriefTheme',
    colors: {
        primary: '000000',
        accent: 'D14F72',
        tertiary: '6a6c75',
        divider: 'd9d9d9',
        insight: 'e6416a',
        titlePage: {
            backgroundColor: '#FFFFFF',
            textColor: '#000000',
        },
        tableLabelBackgroundColor: '#9A9CA5',
        ascendingMetric: '#009855',
        descendingMetric: '#CE0000',
    },
    fonts: {
        heading1: 'Calibri',
        heading2: 'Calibri',
        body: 'Arial',
        titlePage: {
            title: 'Calibri',
            subtitle: 'Arial',
            body: 'Arial',
        },
    },
    sizes: {
        heading1: 22,
        heading2: 14,
        body: 11,
        titlePage: {
            title: 36,
            subtitle: 13.5,
            body: 8.25,
        },
    },
};

function pageOneTemplate (documentContent: DocumentContent): Template[] {
    return [...executiveSummary(documentContent)];
}

function pageTwoTemplate (documentContent: DocumentContent) {
    const { numberOfGuides, guides } = documentContent;

    return [
        ...onboardingUsersWithInAppGuidance(numberOfGuides, guides),
        ...keyOnboardingResults(documentContent.onboardingEventMetrics),
        ...costAndTimeSavings(),
    ];
}

function pageThreeTemplate (documentContent: DocumentContent) {
    return [...whyTheseResultsMatter(documentContent.onboardingEventMetrics)];
}

function pageFourTemplate (documentContent: DocumentContent) {
    const { numberOfGuides, guides } = documentContent;

    return [
        ...generateAppendix(
            numberOfGuides,
            guides,
            documentContent.dayCount,
            Object.values(documentContent.onboardingEventMetrics.perEventMetrics),
        ),
    ];
}

export async function generateBlob (
    data: ImpactBriefData,
    theme: Theme = impactBriefTheme,
    includeHeader: boolean = false,
) {
    const { title, subtitle, content, guides } = data;

    if (typeof content !== 'undefined') {
        const getSuffixForOnboardingType = (
            viewCounts: ViewCounts,
            onboardingEventMetrics: OnboardingEventMetrics,
        ): string => {
            if (viewCounts.totalAccountsOrVisitors === 1) {
                return onboardingEventMetrics.metricType === 'users' ? 'user' : 'account';
            }

            return onboardingEventMetrics.metricType;
        };

        const flattenedContent: Record<string, string | number | boolean | { name: string }[]> = {
            ...Object.fromEntries(
                Object.entries(content).map(([key, value]) => {
                    if (key === 'timeToCompleteMetrics' || key === 'onboardingMetrics' || key === 'retentionMetrics') {
                        if (typeof value === 'object' && value !== null) {
                            return [key, value];
                        }

                        return [key, value];
                    }

                    return [key, value];
                }),
            ),
            totalVisitorsInfluencedSuffix: content.influencedMetrics.count === 1 ? '' : 's',
            numberOfGuidesWithGoalsSuffix: content.numberOfGuidesWithGoals === 1 ? '' : 's',
            numberOfGuidesSuffix: content.numberOfGuides === 1 ? '' : 's',
            engineeringHoursSavedSuffix: content.engineeringHoursSaved === 1 ? '' : 's',
            totalNewVisitorsFirstComparisonRangeSuffix:
                content.onboardingEventMetrics.totalNewVisitorsFirstComparisonRange === '1' ? '' : 's',
            totalNewAccountsFirstComparisonRangeSuffix:
                content.onboardingEventMetrics.totalNewAccountsFirstComparisonRange === '1' ? '' : 's',
            influencedMetricsSuffix: content.influencedMetrics.count === 1 ? '' : 's',
            influencedMetrics: smartFormat(content.influencedMetrics.count),
            influencedMetricsType: content.influencedMetrics.type,
            viewCounts: smartFormat(content.viewCounts.sumViewCount),
            viewCountsSuffix: content.viewCounts.sumViewCount === 1 ? '' : 's',
            totalAccountsOrVisitors: smartFormat(content.viewCounts.totalAccountsOrVisitors),
            totalAccountsOrVisitorsSuffix: content.viewCounts.totalAccountsOrVisitors === 1 ? '' : 's',
            viewCountsType: content.viewCounts.type,
            onboardingType: content.onboardingEventMetrics.metricType,
            onboardingTypeSuffix: getSuffixForOnboardingType(content.viewCounts, content.onboardingEventMetrics),
        };

        if (guides && guides.length > 0) {
            guides.forEach((guide, index) => {
                flattenedContent[`guides[${index}].name`] = guide.name;
            });
        }
        const pageOne = pageOneTemplate(content);
        const pageTwo = pageTwoTemplate(content);
        const pageThree = pageThreeTemplate(content);
        const pageFour = pageFourTemplate(content);

        const stringifiedContent = Object.fromEntries(
            Object.entries(flattenedContent).map(([key, value]) => {
                return [key, String(value)];
            }),
        );
        const docContentPageOne = generateDocumentFromTemplate(pageOne, stringifiedContent);
        const docContentPageTwo = generateDocumentFromTemplate(pageTwo, stringifiedContent);
        const docContentPageThree = generateDocumentFromTemplate(pageThree, stringifiedContent);
        const docContentPageFour = generateDocumentFromTemplate(pageFour, stringifiedContent);

        const styles = createStyles(theme);
        const doc = new Document({
            styles,
            sections: [
                {
                    ...createTitlePage(
                        {
                            title,
                            ...(subtitle && { subtitle }),
                            documentMetaData: {
                                date: new Date(),
                            },
                        },
                        impactBriefTheme,
                    ),
                },
                {
                    properties: {
                        type: SectionType.NEXT_PAGE,
                    },
                    ...(includeHeader && {
                        headers: {
                            default: createPageHeader(title, theme),
                        },
                    }),
                    children: [...docContentPageOne],
                },
                {
                    properties: {
                        type: SectionType.NEXT_PAGE,
                    },
                    ...(includeHeader && {
                        headers: {
                            default: createPageHeader(title, theme),
                        },
                    }),
                    children: [...docContentPageTwo],
                },
                {
                    properties: {
                        type: SectionType.NEXT_PAGE,
                    },
                    ...(includeHeader && {
                        headers: {
                            default: createPageHeader(title, theme),
                        },
                    }),
                    children: [...docContentPageThree],
                },
                {
                    properties: {
                        type: SectionType.NEXT_PAGE,
                    },
                    ...(includeHeader && {
                        headers: {
                            default: createPageHeader(title, theme),
                        },
                    }),
                    children: [...docContentPageFour],
                },
            ],
        });

        const blob = await Packer.toBlob(doc);

        return blob;
    }

    return undefined;
}

export function executiveSummary (documentContent: DocumentContent): Template[] {
    const onboardingMetrics = documentContent.onboardingEventMetrics;
    const { perEventMetrics } = onboardingMetrics;
    const { startFirstPeriod, endFirstPeriod } = documentContent;

    const sortedOnoardingSuccessEvents = Object.values(perEventMetrics).sort((a, b) => {
        return sortOnboardingMetricsEntries(a, b, 'onboardingMetrics', 'onboardingPercentChange');
    });

    const sortedTimeToCompleteEvents = Object.values(perEventMetrics).sort((a, b) => {
        return sortOnboardingMetricsEntries(a, b, 'timeToCompleteMetrics', 'timeToValuePercentComparison');
    });

    const sortedRetentionEvents = Object.values(perEventMetrics).sort((a, b) => {
        return sortOnboardingMetricsEntries(a, b, 'retentionMetrics', 'onboardingRetentionPercentDifference');
    });

    const mostSuccessfulOnboardingEvent = sortedOnoardingSuccessEvents[0];
    const mostSuccessfulTimeToCompleteEvent = sortedTimeToCompleteEvents[0];
    const mostSuccessfulRetentionEvent = sortedRetentionEvents[0];

    const onboardingSentiment = mostSuccessfulOnboardingEvent.onboardingMetrics.sentiment;
    const timeToCompleteSentiment = mostSuccessfulTimeToCompleteEvent.timeToCompleteMetrics.sentiment;
    const retentionSentiment = mostSuccessfulRetentionEvent.retentionMetrics.sentiment;

    const getMetricSymbol = (sentiment: string | undefined, metric: number | undefined): string => {
        if (sentiment === 'positive') {
            return '+';
        }

        if (sentiment === 'neutral') {
            return metric && metric > 0 ? '+' : '';
        }

        return '';
    };

    const getSentimentColor = (sentiment: string | undefined): string | undefined => {
        if (sentiment === 'positive') {
            return impactBriefTheme.colors.ascendingMetric;
        }

        return sentiment === 'negative' ? impactBriefTheme.colors.descendingMetric : impactBriefTheme.colors.primary;
    };

    return [
        {
            function: createHeading,
            args: {
                text: 'Executive Summary',
                level: HEADING_1,
                pageBreakBefore: true,
                color: impactBriefTheme.colors.primary,
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text:
                            'Reviewing our onboarding process revealed that new users had difficulty getting started, delaying engagement with core features.',
                    },
                ],
                breakValue: 1,
                spacing: {
                    after: convertInchesToTwip(0.1),
                },
                textSize: 10,
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text:
                            'This brief highlights how improvements to our onboarding experience with Pendo have driven measurable impact across activation, efficiency, and retention.',
                    },
                ],
                textSize: 10,
                breakValue: 1,
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text: '📅 Reporting period: ',
                        color: impactBriefTheme.colors.tertiary,
                        bold: true,
                    },
                    {
                        text: `${startFirstPeriod} – ${endFirstPeriod}`,
                        color: impactBriefTheme.colors.tertiary,
                    },
                ],
                textSize: 10,
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text: '–––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––',
                        color: impactBriefTheme.colors.divider,
                    },
                ],
            },
        },
        {
            function: createHeading,
            args: {
                text: 'Key outcomes',
                level: HEADING_2,
                color: impactBriefTheme.colors.accent,
            },
        },
        {
            function: createBulletList,
            args: {
                items: [
                    {
                        text: [
                            {
                                text: 'Adoption & reach: {{totalAccountsOrVisitors}} {{onboardingTypeSuffix}} ',
                                bold: true,
                            },
                            {
                                text: 'engaged with onboarding guides, which resulted in ',
                            },
                            {
                                text: '{{viewCounts}} view{{viewCountsSuffix}} ',
                                bold: true,
                            },
                            {
                                text: 'and',
                            },
                            {
                                text: ' {{influencedMetrics}} {{onboardingTypeSuffix}} ',
                                bold: true,
                            },
                            {
                                text: 'adopting core functionality.',
                            },
                        ],
                    },
                    {
                        text: [
                            {
                                text: 'Onboarding success: ',
                                bold: true,
                            },
                            {
                                text: `Among all success events, ${mostSuccessfulOnboardingEvent.keyOnboardingEvent} led to the `,
                            },
                            {
                                text: `greatest improvement in onboarding success (${getMetricSymbol(
                                    onboardingSentiment,
                                    mostSuccessfulOnboardingEvent.onboardingMetrics.onboardingPercentChange,
                                )}${mostSuccessfulOnboardingEvent.onboardingMetrics.onboardingPercentChange}%)`,
                                bold: true,
                                color: getSentimentColor(onboardingSentiment),
                            },
                            {
                                text: ', indicating it’s the most effective action for completing onboarding.',
                            },
                        ],
                    },
                    {
                        text: [
                            {
                                text: 'Time to value: ',
                                bold: true,
                            },
                            {
                                text: `Among all success events, ${mostSuccessfulTimeToCompleteEvent.keyOnboardingEvent} led to the`,
                            },
                            {
                                text: ` greatest improvement in time to value (${getMetricSymbol(
                                    timeToCompleteSentiment,
                                    mostSuccessfulTimeToCompleteEvent.timeToCompleteMetrics
                                        .timeToValuePercentComparison,
                                )}${
                                    mostSuccessfulTimeToCompleteEvent.timeToCompleteMetrics.timeToValuePercentComparison
                                }%)`,
                                bold: true,
                                color: getSentimentColor(timeToCompleteSentiment),
                            },
                            {
                                text: ', indicating the fastest route to onboarding completion.',
                            },
                        ],
                    },
                    {
                        text: [
                            {
                                text: 'Retention impact: ',
                                bold: true,
                            },
                            {
                                text: `Users who interacted with the ${mostSuccessfulRetentionEvent.keyOnboardingEvent} event showed the `,
                            },
                            {
                                text: `highest retention increase (${getMetricSymbol(
                                    retentionSentiment,
                                    mostSuccessfulRetentionEvent.retentionMetrics.onboardingRetentionPercentDifference,
                                )}${
                                    mostSuccessfulRetentionEvent.retentionMetrics.onboardingRetentionPercentDifference
                                }%)`,
                                bold: true,
                                color: getSentimentColor(retentionSentiment),
                            },
                            {
                                text:
                                    ', making it the most effective onboarding action for driving long-term user engagement.',
                            },
                        ],
                    },
                ],
            },
        },
        {
            function: createParagraph,
            args: {
                text: [{ text: '' }],
                breakValue: 1,
            },
        },
        {
            function: createHeading,
            args: {
                text: 'Operational benefits',
                level: HEADING_2,
                color: impactBriefTheme.colors.accent,
            },
        },
        {
            function: createBulletList,
            args: {
                items: [
                    {
                        text: [
                            {
                                text: 'Cost savings: ',
                                bold: true,
                            },
                            {
                                text: 'Estimated ',
                            },
                            {
                                text: '{{guideCostSavings}} ',
                                bold: true,
                            },
                            {
                                text: 'saved by reducing custom onboarding development.',
                            },
                            { text: '5', superScript: true, color: defaultTheme.colors.accent },
                        ],
                    },
                    {
                        text: [
                            {
                                text: 'Time savings: ',
                                bold: true,
                            },
                            {
                                text: 'Estimated ',
                            },
                            {
                                text: '{{engineeringHoursSaved}} hour{{engineeringHoursSavedSuffix}} ',
                                bold: true,
                            },
                            {
                                text:
                                    'by automating key steps, making the process more efficient and easier to manage.',
                            },
                            { text: '6', superScript: true, color: defaultTheme.colors.accent },
                        ],
                    },
                ],
            },
        },
        {
            function: createParagraph,
            args: {
                text: [{ text: '' }],
                breakValue: 1,
            },
        },
        {
            function: createHeading,
            args: {
                text: 'Strategic opportunity',
                level: HEADING_2,
                color: impactBriefTheme.colors.accent,
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text:
                            'By increasing onboarding completion, we can meaningfully increase user retention and reduce early churn. A targeted strategy using Pendo’s segmentation and reporting tools is in place to track, re-engage, and optimize onboarding over time.',
                    },
                ],
            },
        },
    ];
}

export function onboardingUsersWithInAppGuidance (
    numberOfGuidesWithGoals: number,
    guides: Array<{ name: string }>,
): Template[] {
    return [
        {
            function: createHeading,
            args: {
                text: 'Accelerating onboarding and reducing costs',
                level: HEADING_1,
                color: impactBriefTheme.colors.primary,
                pageBreakBefore: true,
            },
        },
        {
            function: createStylizedSubHeading,
            args: {
                text: 'Strategy: Onboard users with in-app guidance',
                headingType: 'section',
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text: `We launched ${guides.length} onboarding guides in Pendo, including ${numberOfGuidesWithGoals} with adoption goals tied to critical product milestones. These were the results:`,
                    },
                ],
                textSize: 10,
                spacing: {
                    before: convertInchesToTwip(0.15),
                },
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text: ' 📅 Date range: ',
                        bold: true,
                    },
                    {
                        text: '{{startSecondPeriod}} – {{endFirstPeriod}}',
                    },
                ],
                textSize: 10,
            },
        },
        {
            function: createBulletList,
            args: {
                items: [
                    {
                        text: [
                            {
                                text: '{{totalAccountsOrVisitors}} {{onboardingTypeSuffix}} reached',
                                bold: true,
                            },
                            { text: ', generating ' },
                            { text: '{{viewCounts}} guide view{{viewCountsSuffix}}', bold: true },
                            { text: '.' },
                        ],
                    },
                    ...(numberOfGuidesWithGoals
                        ? [
                            {
                                text: [
                                    {
                                        text: '{{influencedMetrics}} {{onboardingTypeSuffix}}',
                                        bold: true,
                                    },
                                    { text: ' adopted core functionality after viewing these guides.' },
                                    { text: '*', superScript: true, color: defaultTheme.colors.accent },
                                ],
                            },
                        ]
                        : []),
                ],
                spacing: {
                    after: convertInchesToTwip(0.15),
                },
            },
        },
    ];
}

export function keyOnboardingResults (onboardingEventMetrics: OnboardingEventMetrics): Template[] {
    const {
        startFirstPeriod,
        startSecondPeriod,
        endFirstPeriod,
        endSecondPeriod,
        segmentName,
        perEventMetrics,
    } = onboardingEventMetrics;

    const sortedOnoardingSuccessEvents = Object.values(perEventMetrics).sort((a, b) => {
        return sortOnboardingMetricsEntries(a, b, 'onboardingMetrics', 'onboardingPercentChange');
    });

    const sortedTimeToCompleteEvents = Object.values(perEventMetrics).sort((a, b) => {
        return sortOnboardingMetricsEntries(a, b, 'timeToCompleteMetrics', 'timeToValuePercentComparison');
    });

    const mostSuccessfulOnboardingEvent = sortedOnoardingSuccessEvents[0];
    const mostSuccessfulTimeToCompleteEvent = sortedTimeToCompleteEvents[0];

    const onboardingSentiment = mostSuccessfulOnboardingEvent.onboardingMetrics.sentiment;
    const timeToCompleteSentiment = mostSuccessfulTimeToCompleteEvent.timeToCompleteMetrics.sentiment;

    const onboardingSuccessTableData = sortedOnoardingSuccessEvents.map((event) => {
        return formatOnboardingSuccessTable(event);
    });

    const onboardingTimeToCompleteTableData = sortedTimeToCompleteEvents.map((event) => {
        return formatOnboardingTimeToCompleteTable(event);
    });

    return [
        {
            function: createStylizedSubHeading,
            args: {
                text: 'Key results',
                headingType: 'section',
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text:
                            'More users are adopting core features. Onboarding improvements have led to increased engagement with core functionality, helping users get more out of the product.',
                    },
                ],
                textSize: 10,
                spacing: {
                    before: convertInchesToTwip(0.15),
                },
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text: '📅 Date range: ',
                        bold: true,
                    },
                    {
                        text: `Comparing ${startSecondPeriod} – ${endSecondPeriod} vs. ${startFirstPeriod} – ${endFirstPeriod}`,
                    },
                ],
                textSize: 10,
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text: '👥 Segment: ',
                        bold: true,
                    },
                    {
                        text: segmentName,
                    },
                ],
            },
        },
        {
            function: createStylizedSubHeading,
            args: {
                text: 'Onboarding success',
                headingType: 'subSection',
            },
        },
        ...insightsBlurb(
            {
                function: createParagraph,
                args: {
                    text: [
                        {
                            text: 'Insight: ',
                            bold: true,
                            color: impactBriefTheme.colors.insight,
                        },
                        {
                            text: 'Among all success events, ',
                        },
                        {
                            text: `${mostSuccessfulOnboardingEvent.keyOnboardingEvent}`,
                            bold: true,
                        },
                        {
                            text: ' led to the ',
                        },
                        {
                            text: `greatest improvement in onboarding success (+${mostSuccessfulOnboardingEvent.onboardingMetrics.onboardingPercentChange}%)`,
                            bold: true,
                            color: impactBriefTheme.colors.ascendingMetric,
                        },
                        {
                            text: ', indicating it’s the most effective action for onboarding completion.',
                        },
                    ],
                    spacing: {
                        before: convertInchesToTwip(0.15),
                    },
                },
            },
            onboardingSentiment,
        ),
        {
            function: createMultiEventTable,
            args: {
                multiEventData: {
                    labels: ['Success event', '% Change', 'Current', 'Previous'],
                    values: onboardingSuccessTableData,
                },
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text:
                            'Calculation: Percentage of newly identified users who completed onboarding within their first month, from first identification to their first onboarding success event.',
                        italics: true,
                        color: impactBriefTheme.colors.tertiary,
                    },
                ],
                textSize: 10,
                breakValue: 1,
                spacing: {
                    before: convertInchesToTwip(0.15),
                },
            },
        },
        {
            function: createParagraph,
            args: {
                text: [{ text: '' }],
                newPage: true,
            },
        },
        {
            function: createStylizedSubHeading,
            args: {
                text: 'Time to value',
                headingType: 'subSection',
            },
        },
        ...insightsBlurb(
            {
                function: createParagraph,
                args: {
                    text: [
                        {
                            text: 'Insight: ',
                            bold: true,
                            color: impactBriefTheme.colors.insight,
                        },
                        {
                            text: 'Among all success events, ',
                        },
                        {
                            text: `${mostSuccessfulTimeToCompleteEvent.keyOnboardingEvent}`,
                            bold: true,
                        },
                        {
                            text: ' led to ',
                        },
                        {
                            text: `greatest improvement in time to value (+${mostSuccessfulTimeToCompleteEvent.timeToCompleteMetrics.timeToValuePercentComparison}%)`,
                            bold: true,
                            color: impactBriefTheme.colors.ascendingMetric,
                        },
                        {
                            text: ', indicating the fastest route to onboarding completion.',
                        },
                    ],
                    spacing: {
                        before: convertInchesToTwip(0.15),
                    },
                },
            },
            timeToCompleteSentiment,
        ),
        {
            function: createMultiEventTable,
            args: {
                multiEventData: {
                    labels: ['Success event', '% Change', 'Current time to value', 'Previous time to value'],
                    values: onboardingTimeToCompleteTableData,
                },
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text:
                            'Calculation: Median time from when a user is first identified to their first onboarding interaction. Calculated per visitor for the selected date range and compared to a prior range to show change over time.',
                        italics: true,
                        color: impactBriefTheme.colors.tertiary,
                    },
                ],
                textSize: 10,
                breakValue: 1,
                spacing: {
                    before: convertInchesToTwip(0.15),
                    after: convertInchesToTwip(0.15),
                },
            },
        },
    ];
}

export function opportunitiesForImprovement (
    onboardingMetrics: OnboardingMetrics,
    retentionMetrics: RetentionMetrics,
    timeToCompleteMetrics: TimeToCompleteMetrics,
    dayCount: number,
): Template[] {
    const sectionContent: {
        text: Array<{ text: string; bold?: boolean; superScript?: boolean; color?: string }>;
        level?: number;
        textSize?: number;
    }[] = [];

    let negativeNeutralMetricsExist = false;

    const formattedOnboardingMetrics = {
        completedCountBefore: smartFormat(onboardingMetrics.completedCountBefore),
        newCountBefore: smartFormat(onboardingMetrics.newCountBefore),
        completedCountAfter: smartFormat(onboardingMetrics.completedCountAfter),
        newCountAfter: smartFormat(onboardingMetrics.newCountAfter),
    };

    if (onboardingMetrics.sentiment === 'negative') {
        negativeNeutralMetricsExist = true;
        sectionContent.push(
            {
                text: [
                    { text: 'Onboarding success rates have dropped from ' },
                    {
                        text: `${onboardingMetrics.percentageCompleteFirstComparisonRange}% to ${onboardingMetrics.percentageCompleteSecondComparisonRange}%`,
                        bold: true,
                    },
                    { text: '.' },
                    { text: '†', superScript: true, color: defaultTheme.colors.accent },
                ],
                level: 0,
            },
            {
                text: [
                    {
                        text: `Previous: ${onboardingMetrics.percentageCompleteFirstComparisonRange}% | ${formattedOnboardingMetrics.completedCountBefore} out of ${formattedOnboardingMetrics.newCountBefore} new {{onboardingType}} within the first ${dayCount} days`,
                        color: defaultTheme.colors.tertiary,
                    },
                ],
                level: 1,
                textSize: 10,
            },
            {
                text: [
                    {
                        text: `Current: ${onboardingMetrics.percentageCompleteSecondComparisonRange}% | ${formattedOnboardingMetrics.completedCountAfter} out of ${formattedOnboardingMetrics.newCountAfter} new {{onboardingType}} within the first ${dayCount} days`,
                        color: defaultTheme.colors.tertiary,
                    },
                ],
                level: 1,
                textSize: 10,
            },
        );
    }
    if (onboardingMetrics.sentiment === 'neutral') {
        negativeNeutralMetricsExist = true;
        sectionContent.push(
            {
                text: [
                    { text: 'Onboarding success rates stayed about the same at ' },
                    { text: `${onboardingMetrics.onboardingPercentChange}%`, bold: true },
                    { text: '.' },
                    { text: '†', superScript: true, color: defaultTheme.colors.accent },
                ],
                level: 0,
            },
            {
                text: [
                    {
                        text: `Previous: ${onboardingMetrics.percentageCompleteFirstComparisonRange}% | ${formattedOnboardingMetrics.completedCountBefore} out of ${formattedOnboardingMetrics.newCountBefore} new {{onboardingType}} within the first ${dayCount} days`,
                        color: defaultTheme.colors.tertiary,
                    },
                ],
                level: 1,
                textSize: 10,
            },
            {
                text: [
                    {
                        text: `Current: ${onboardingMetrics.percentageCompleteSecondComparisonRange}% | ${formattedOnboardingMetrics.completedCountAfter} out of ${formattedOnboardingMetrics.newCountAfter} new {{onboardingType}} within the first ${dayCount} days`,
                        color: defaultTheme.colors.tertiary,
                    },
                ],
                level: 1,
                textSize: 10,
            },
        );
    }

    if (timeToCompleteMetrics.sentiment === 'negative') {
        negativeNeutralMetricsExist = true;
        sectionContent.push(
            {
                text: [
                    { text: 'New {{onboardingType}} onboard slower, with median time to completion ' },
                    { text: `increased by ${timeToCompleteMetrics.timeToValuePercentComparison}%`, bold: true },
                    { text: '.' },
                ],
                level: 0,
            },
            {
                text: [
                    {
                        text: `Previous: ${timeToCompleteMetrics.timeToValueFirstComparisonRange}`,
                        color: defaultTheme.colors.tertiary,
                    },
                ],
                level: 1,
                textSize: 10,
            },
            {
                text: [
                    {
                        text: `Current: ${timeToCompleteMetrics.timeToValueSecondComparisonRange}`,
                        color: defaultTheme.colors.tertiary,
                    },
                ],
                level: 1,
                textSize: 10,
            },
        );
    }

    if (timeToCompleteMetrics.sentiment === 'neutral') {
        negativeNeutralMetricsExist = true;
        sectionContent.push(
            {
                text: [{ text: 'New {{onboardingType}} are onboarding in roughly the same amount of time as before.' }],
                level: 0,
            },
            {
                text: [
                    {
                        text: `Previous: ${timeToCompleteMetrics.timeToValueFirstComparisonRange}`,
                        color: defaultTheme.colors.tertiary,
                    },
                ],
                level: 1,
                textSize: 10,
            },
            {
                text: [
                    {
                        text: `Current: ${timeToCompleteMetrics.timeToValueSecondComparisonRange}`,
                        color: defaultTheme.colors.tertiary,
                    },
                ],
                level: 1,
                textSize: 10,
            },
        );
    }

    if (retentionMetrics.sentiment === 'negative' && retentionMetrics.completedOnboardingRetentionPercent !== 0) {
        negativeNeutralMetricsExist = true;
        sectionContent.push({
            text: [
                { text: 'Retention declined, with only ' },
                { text: `${retentionMetrics.completedOnboardingRetentionPercent}% `, bold: true },
                { text: 'of {{onboardingType}} who completed onboarding retained, down from ' },
                { text: `${retentionMetrics.incompleteOnboardingRetentionPercent}% `, bold: true },
                { text: "among those who didn't." },
                { text: '††', superScript: true, color: defaultTheme.colors.accent },
            ],
            level: 0,
        });
    }

    if (retentionMetrics.sentiment === 'neutral' && retentionMetrics.completedOnboardingRetentionPercent !== 0) {
        negativeNeutralMetricsExist = true;
        sectionContent.push({
            text: [
                { text: 'Retention held steady, with ' },
                { text: `${retentionMetrics.completedOnboardingRetentionPercent}% `, bold: true },
                { text: ' of {{onboardingType}} who completed onboarding retained, the same as the ' },
                { text: `${retentionMetrics.incompleteOnboardingRetentionPercent}% `, bold: true },
                { text: "for those who didn't." },
                { text: '††', superScript: true, color: defaultTheme.colors.accent },
            ],
            level: 0,
        });
    }

    const opportunitiesForImprovement: Template[] = negativeNeutralMetricsExist
        ? [
            {
                function: createHeading,
                args: {
                    text: 'Opportunities for improvement',
                    level: HEADING_2,
                    color: impactBriefTheme.colors.accent,
                    breakValue: 1,
                },
            },
            {
                function: createParagraph,
                args: {
                    text: [
                        {
                            text:
                                  'Date range: Comparing {{startSecondPeriod}} – {{endSecondPeriod}} vs. {{startFirstPeriod}} – {{endFirstPeriod}}',
                            bold: true,
                        },
                    ],
                    textSize: 10,
                },
            },
            {
                function: createBulletList,
                args: {
                    items: sectionContent,
                },
            },
        ]
        : [];

    return opportunitiesForImprovement;
}

export function costAndTimeSavings (): Template[] {
    return [
        {
            function: createStylizedSubHeading,
            args: {
                text: 'Cost and time savings',
                headingType: 'section',
            },
        },
        {
            function: createBulletList,
            args: {
                items: [
                    {
                        text: [
                            { text: 'Operationally, we saved an estimated ' },
                            { text: '{{guideCostSavings}} ', bold: true },
                            { text: 'by reducing custom onboarding development.' },
                            { text: '¶', superScript: true, color: defaultTheme.colors.accent },
                        ],
                    },
                    {
                        text: [
                            { text: 'And by automating key steps, we freed up ' },
                            {
                                text: '{{engineeringHoursSaved}} hour{{engineeringHoursSavedSuffix}}',
                                bold: true,
                            },
                            { text: ', making the process faster and easier to maintain.' },
                            { text: '§', superScript: true, color: defaultTheme.colors.accent },
                        ],
                    },
                ],
                spacing: {
                    before: convertInchesToTwip(0.15),
                },
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text:
                            'These results reflect meaningful improvements in how we activate {{onboardingType}} and measure onboarding success.',
                        color: impactBriefTheme.colors.primary,
                    },
                ],
                breakValue: 1,
            },
        },
    ];
}

export function whyTheseResultsMatter (onboardingEventMetrics: OnboardingEventMetrics): Template[] {
    const sortedRetentionEvents = Object.values(onboardingEventMetrics.perEventMetrics).sort((a, b) => {
        return sortOnboardingMetricsEntries(a, b, 'retentionMetrics', 'onboardingRetentionPercentDifference');
    });

    const onboardingRetentionTableData = sortedRetentionEvents.map((event) => {
        return formatOnboardingRetentionTable(event);
    });

    const mostSuccessfulRetentionEvent = sortedRetentionEvents[0];
    const retentionSentiment = mostSuccessfulRetentionEvent.retentionMetrics.sentiment;

    return [
        {
            function: createHeading,
            args: {
                text: 'Why these results matter',
                level: HEADING_1,
                pageBreakBefore: true,
                color: impactBriefTheme.colors.primary,
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text:
                            'Our data shows that users who complete onboarding retain at a higher rate than those who don’t. This reinforces onboarding as a key factor contributing to long-term product engagement.',
                    },
                ],
                spacing: {
                    after: convertInchesToTwip(0.15),
                },
            },
        },
        {
            function: createStylizedSubHeading,
            args: {
                text: 'RETENTION BY ONBOARDING',
                headingType: 'subSection',
            },
        },
        ...insightsBlurb(
            {
                function: createParagraph,
                args: {
                    text: [
                        {
                            text: 'Insight: ',
                            bold: true,
                            color: impactBriefTheme.colors.insight,
                        },
                        {
                            text: 'Users who interacted with the ',
                        },
                        {
                            text: `${mostSuccessfulRetentionEvent.keyOnboardingEvent}`,
                            bold: true,
                        },
                        {
                            text: ' showed the',
                        },
                        {
                            text: ` highest retention increase (+${mostSuccessfulRetentionEvent.retentionMetrics.onboardingRetentionPercentDifference}%)`,
                            bold: true,
                            color: impactBriefTheme.colors.ascendingMetric,
                        },
                        {
                            text:
                                ' making it the most effective onboarding action for driving long-term user engagement.',
                        },
                    ],
                    spacing: {
                        before: convertInchesToTwip(0.15),
                        after: convertInchesToTwip(0.1),
                    },
                },
            },
            retentionSentiment,
        ),
        {
            function: createMultiEventTable,
            args: {
                multiEventData: {
                    labels: ['Success event', '% Change', 'Retention (Completed)', 'Retention (Incomplete)'],
                    values: onboardingRetentionTableData,
                },
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text:
                            'Calculation for retention: Percentage of newly identified users who returned to the application in their third month after being first identified.',
                        italics: true,
                        color: impactBriefTheme.colors.tertiary,
                    },
                ],
                spacing: {
                    before: convertInchesToTwip(0.15),
                    after: convertInchesToTwip(0.15),
                },
            },
        },
        ...strategicOpportunity(sortedRetentionEvents),
        ...suggestedActions(onboardingEventMetrics),
    ];
}

export function strategicOpportunity (sortedRetentionEvents: EventMetrics[]): Template[] {
    const mostSuccessfulRetentionEvent = sortedRetentionEvents[0];
    const lowestAscendingRetentionEvent = sortedRetentionEvents.reduce(
        (previous: EventMetrics, current: EventMetrics) => {
            const previousValue = previous.retentionMetrics.onboardingRetentionPercentGoal;
            const currentValue = current.retentionMetrics.onboardingRetentionPercentGoal;

            if (previousValue === undefined || currentValue === undefined) {
                return previous;
            }

            return previousValue < currentValue ? previous : current;
        },
        mostSuccessfulRetentionEvent,
    );

    return [
        {
            function: createStylizedSubHeading,
            args: {
                text: 'Strategic opportunity',
                headingType: 'section',
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text: 'Across all events, if we increase onboarding completion to ',
                    },
                    {
                        text: `${mostSuccessfulRetentionEvent.retentionMetrics.onboardingRetentionPercentGoal}%`,
                        bold: true,
                    },
                    {
                        text: ` for ${lowestAscendingRetentionEvent.keyOnboardingEvent}, we estimate that `,
                    },
                    {
                        text: `${mostSuccessfulRetentionEvent.retentionMetrics.retentionEstimatedUsersCount} additional users each month `,
                        bold: true,
                    },
                    {
                        text: 'would enter the higher-retention group:',
                    },
                ],
                spacing: {
                    before: convertInchesToTwip(0.15),
                },
            },
        },
        {
            function: createBulletList,
            args: {
                items: [
                    {
                        text: [
                            {
                                text: '[#] users × ([A]% – [B]%) = [Estimated retained users]',
                            },
                        ],
                    },
                ],
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text:
                            'This improvement represents a clear, high-impact opportunity to strengthen app retention and reduce early churn.',
                    },
                ],
                breakValue: 1,
                spacing: {
                    after: convertInchesToTwip(0.15),
                },
            },
        },
    ];
}

export function suggestedActions (onboardingEventMetrics: OnboardingEventMetrics): Template[] {
    const getPositiveCountForEvent = (event: EventMetrics): number => {
        const { onboardingMetrics, retentionMetrics, timeToCompleteMetrics } = event;

        return [onboardingMetrics.sentiment, retentionMetrics.sentiment, timeToCompleteMetrics.sentiment].filter(
            (sentiment) => sentiment === 'positive',
        ).length;
    };

    const positiveCount = Object.values(onboardingEventMetrics.perEventMetrics).reduce((count, event) => {
        return count + getPositiveCountForEvent(event);
    }, 0);

    const numberOfEvents = Object.keys(onboardingEventMetrics.perEventMetrics).length;

    // If at least 2/3 of the event metrics are positive, we show the ascending suggested actions
    const showAscendingSuggestedActions = positiveCount / numberOfEvents >= (2 / 3) * numberOfEvents;

    const sortedRetentionEvents = Object.values(onboardingEventMetrics.perEventMetrics).sort((a, b) => {
        return sortOnboardingMetricsEntries(a, b, 'retentionMetrics', 'onboardingRetentionPercentDifference');
    });

    const { retentionMetrics } = sortedRetentionEvents[0];

    const positive: Template[] = [
        {
            function: createStylizedSubHeading,
            args: {
                text: 'Suggested actions',
                headingType: 'section',
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text:
                            'To maintain momentum and continue improving onboarding outcomes, consider the following:',
                        color: impactBriefTheme.colors.primary,
                    },
                ],
                spacing: {
                    before: convertInchesToTwip(0.5),
                },
            },
        },
        {
            function: createBulletList,
            args: {
                items: [
                    {
                        text: [
                            {
                                text: "Repurpose what's working. ",
                                bold: true,
                            },
                            {
                                text: 'Duplicate a top-performing guide and apply it in a new area of the product.',
                            },
                        ],
                    },
                    {
                        text: [
                            {
                                text: 'Refine your best guides with A/B testing. ',
                                bold: true,
                            },
                            {
                                text:
                                    'Experiment with copy, timing, and design to improve engagement. For more information, see ',
                            },
                            {
                                text: 'A/B test with guide experiments ›',
                                hyperlink:
                                    'https://support.pendo.io/hc/en-us/articles/37754430083611-A-B-test-with-guide-experiments-beta',
                                color: defaultTheme.colors.accent,
                                underline: true,
                                bold: true,
                            },
                        ],
                    },
                    {
                        text: [
                            {
                                text: 'Collect qualitative feedback. ',
                                bold: true,
                            },
                            {
                                text:
                                    'Add a poll at the end of an onboarding guide to learn what worked and what didn’t—then use that input to inform future iterations. \n(Incorporates the suggestion clearly and keeps it task-focused)',
                            },
                        ],
                    },
                    {
                        text: [
                            {
                                text: 'Improve onboarding completion to retain more users. ',
                                bold: true,
                            },
                            {
                                text: `Each ${retentionMetrics.retentionTotalUsersCount} new {{onboardingType}} × (${retentionMetrics.onboardingRetentionPercentGoal}% - ${retentionMetrics.completedOnboardingRetentionPercent}%) = ${retentionMetrics.retentionEstimatedUsersCount} additional {{onboardingType}} retained every ${retentionMetrics.onboardingLength} days.`,
                            },
                        ],
                    },
                ],
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text:
                            'Improving onboarding completion can help reduce churn, boost long-term engagement, and drive stronger app adoption.',
                        color: impactBriefTheme.colors.primary,
                    },
                ],
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text:
                            'Improving these areas can lead to a smoother onboarding experience and stronger results across adoption and retention.',
                        color: impactBriefTheme.colors.primary,
                    },
                ],
            },
        },
    ];

    const mixed: Template[] = [
        {
            function: createStylizedSubHeading,
            args: {
                text: 'Suggested actions',
                headingType: 'section',
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text: 'To improve onboarding and drive stronger adoption, consider the following:',
                        color: impactBriefTheme.colors.primary,
                    },
                ],
                spacing: {
                    before: convertInchesToTwip(0.15),
                },
            },
        },
        {
            function: createBulletList,
            args: {
                items: [
                    {
                        text: [
                            {
                                text: 'A/B test top-performing guides. ',
                                bold: true,
                            },
                            {
                                text: 'Experiment with copy, timing, and layout to see what increases engagement. ',
                            },
                            {
                                text: 'Learn more about guide experiments ›',
                                hyperlink:
                                    'https://support.pendo.io/hc/en-us/articles/37754430083611-A-B-test-with-guide-experiments-beta',
                                color: defaultTheme.colors.accent,
                                underline: true,
                                bold: true,
                            },
                        ],
                    },
                    {
                        text: [
                            {
                                text: 'Analyze guide performance to identify drop-off points. ',
                                bold: true,
                            },
                            {
                                text:
                                    'Use guide analytics to pinpoint where users lose momentum and adjust those flows.',
                            },
                        ],
                    },
                    {
                        text: [
                            {
                                text: 'Revisit segmentation and guide targeting. ',
                                bold: true,
                            },
                            {
                                text:
                                    'Check if your segments, activation methods, or guide locations are still effective—or if they need refining to match current user behavior.',
                            },
                        ],
                    },
                    {
                        text: [
                            {
                                text: 'Compare results across segments. ',
                                bold: true,
                            },
                            {
                                text:
                                    'Use segmentation to understand which types of users engage more—and tailor experiences to match.',
                            },
                        ],
                    },
                    {
                        text: [
                            {
                                text: 'Pull in additional insights. ',
                                bold: true,
                            },
                            {
                                text:
                                    'Consider using Session Replay or other tools to see how users interact with guides and where they may get stuck.',
                            },
                        ],
                    },
                ],
            },
        },
    ];

    return showAscendingSuggestedActions ? positive : mixed;
}

export function generateAppendix (
    numberOfGuides: number,
    guides: Array<{ name: string }>,
    dayCount: number,
    eventMetrics: EventMetrics[],
): Template[] {
    return [
        {
            function: createHeading,
            args: {
                text: 'Appendix',
                level: HEADING_1,
                pageBreakBefore: true,
                color: impactBriefTheme.colors.primary,
            },
        },
        {
            function: createHeading,
            args: {
                text: 'CALCULATIONS',
                level: HEADING_2,
                color: impactBriefTheme.colors.tertiary,
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    { text: '1 ', superScript: true, color: impactBriefTheme.colors.primary },
                    {
                        text: 'Explanation for key product action completion: ',
                        bold: true,
                        color: impactBriefTheme.colors.primary,
                        textSize: 12,
                    },
                    {
                        text:
                            'We tracked guide goals to measure feature engagement, capturing how many {{onboardingType}} interacted with the related feature after viewing the guide.',
                        color: impactBriefTheme.colors.tertiary,
                        textSize: 10,
                    },
                ],
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    { text: '2 ', superScript: true, color: impactBriefTheme.colors.primary },
                    {
                        text: 'Calculation for onboarding success rate: ',
                        bold: true,
                        color: impactBriefTheme.colors.primary,
                        textSize: 12,
                    },
                    {
                        text: `Percentage of newly identified {{onboardingType}} who completed onboarding within the first ${dayCount} days, from first identification to their first onboarding success event.`,
                        color: impactBriefTheme.colors.tertiary,
                        textSize: 10,
                    },
                ],
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    { text: '3 ', superScript: true, color: impactBriefTheme.colors.primary },
                    {
                        text: 'Calculation for time to value: ',
                        bold: true,
                        color: impactBriefTheme.colors.primary,
                        textSize: 12,
                    },
                    {
                        text:
                            'Median time from when a user is first identified to their first onboarding interaction. Calculated per visitor for the selected date range and compared to a prior range to show change over time.',
                        color: impactBriefTheme.colors.tertiary,
                        textSize: 10,
                    },
                ],
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    { text: '4 ', superScript: true, color: impactBriefTheme.colors.primary },
                    {
                        text: 'Calculation for retention: ',
                        bold: true,
                        color: impactBriefTheme.colors.primary,
                        textSize: 12,
                    },
                    {
                        text:
                            'Percentage of newly identified {{onboardingType}} who returned to the application 90 days after being first identified.',
                        color: impactBriefTheme.colors.tertiary,
                        textSize: 10,
                    },
                ],
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    { text: '5 ', superScript: true, color: impactBriefTheme.colors.primary },
                    {
                        text: 'Calculation for guide cost savings: ',
                        bold: true,
                        color: impactBriefTheme.colors.primary,
                        textSize: 12,
                    },
                    {
                        text:
                            'Estimated cost per guide: $587 without Pendo vs. $136 with Pendo. With {{numberOfGuides}} guide{{numberOfGuidesSuffix}} deployed, this resulted in {{guideCostSavings}} in cost savings.',
                        color: impactBriefTheme.colors.tertiary,
                    },
                ],
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    { text: '6 ', superScript: true, color: impactBriefTheme.colors.primary },
                    {
                        text: 'Calculation for engineering time savings: ',
                        bold: true,
                        color: impactBriefTheme.colors.primary,
                        textSize: 12,
                    },
                    {
                        text:
                            'Estimated development time per guide: 9 hours without Pendo vs. 3 hours with Pendo. Deploying {{numberOfGuides}} guide{{numberOfGuidesSuffix}} saved {{engineeringHoursSaved}} engineering hour{{engineeringHoursSavedSuffix}}.',
                        color: impactBriefTheme.colors.tertiary,
                        textSize: 10,
                    },
                ],
            },
        },
        {
            function: createParagraph,
            args: {
                text: [
                    {
                        text: 'Note: ',
                        bold: true,
                        color: impactBriefTheme.colors.primary,
                    },
                    {
                        text:
                            'These calculations are based on internal engineering benchmarks and Pendo industry estimates.',
                    },
                ],
            },
        },
        ...(numberOfGuides > 0
            ? [
                {
                    function: createParagraph,
                    args: {
                        text: [
                            {
                                text: '_________________________________________________________________________',
                                color: impactBriefTheme.colors.tertiary,
                            },
                        ],
                        breakValue: 1,
                    },
                },
                {
                    function: createHeading,
                    args: {
                        text: 'GUIDE DETAILS',
                        level: HEADING_2,
                        color: impactBriefTheme.colors.tertiary,
                        breakValue: 1,
                    },
                },
                {
                    function: createBulletList,
                    args: {
                        items: [
                            ...guides.map((guide) => ({
                                text: [{ text: guide.name, bold: true }],
                            })),
                        ],
                    },
                },
            ]
            : []),
        {
            function: createHeading,
            args: {
                text: 'EVENT DETAILS',
                level: HEADING_2,
                color: impactBriefTheme.colors.tertiary,
                breakValue: 1,
            },
        },
        {
            function: createBulletList,
            args: {
                items: eventMetrics.map((keyEvent) => {
                    return {
                        text: [{ text: keyEvent.keyOnboardingEvent, bold: true }],
                    };
                }),
            },
        },
    ];
}

function formatOnboardingSuccessTable (keyEvent: EventMetrics): Paragraph[] {
    const { sentiment } = keyEvent.onboardingMetrics;
    const metricTrendSymbol = (sentiment: string) => {
        return sentiment === 'positive' ? '+ ' : '';
    };

    const colorMetric = (sentiment: string) => {
        if (sentiment === 'neutral') {
            return {};
        }

        return sentiment === 'positive'
            ? { color: impactBriefTheme.colors.ascendingMetric }
            : { color: impactBriefTheme.colors.descendingMetric };
    };

    return [
        createTableParagraph({
            text: [
                {
                    text: keyEvent.keyOnboardingEvent,
                },
            ],
            spacing: {
                before: 0,
                after: 0,
            },
        }),
        createTableParagraph({
            text: [
                {
                    text: `${metricTrendSymbol(sentiment)}${keyEvent.onboardingMetrics.onboardingPercentChange}%`,
                    bold: true,
                    ...colorMetric(sentiment),
                },
            ],
            spacing: {
                before: 0,
                after: 0,
            },
        }),
        createTableParagraph({
            text: [
                {
                    text: `${keyEvent.onboardingMetrics.percentageCompleteSecondComparisonRange}%`,
                },
                {
                    text: ` (${keyEvent.onboardingMetrics.completedCountAfter}/${keyEvent.onboardingMetrics.newCountAfter})`,
                    color: defaultTheme.colors.tertiary,
                },
            ],
            spacing: {
                before: 0,
                after: 0,
            },
        }),
        createTableParagraph({
            text: [
                {
                    text: `${keyEvent.onboardingMetrics.percentageCompleteFirstComparisonRange}%`,
                },
                {
                    text: ` (${keyEvent.onboardingMetrics.completedCountBefore}/${keyEvent.onboardingMetrics.newCountBefore})`,
                    color: defaultTheme.colors.tertiary,
                },
            ],
            spacing: {
                before: 0,
                after: 0,
            },
        }),
    ];
}

function formatOnboardingTimeToCompleteTable (keyEvent: EventMetrics): Paragraph[] {
    const { timeToCompleteMetrics } = keyEvent;
    const { sentiment } = timeToCompleteMetrics;

    const colorMetric = (sentiment: string) => {
        if (sentiment === 'neutral') {
            return {};
        }

        return sentiment === 'positive'
            ? { color: impactBriefTheme.colors.ascendingMetric }
            : { color: impactBriefTheme.colors.descendingMetric };
    };
    const metricTrendSymbol = (sentiment: string) => {
        if (sentiment === 'neutral') return '';

        return sentiment === 'positive' ? '+' : '-';
    };

    return [
        createTableParagraph({
            text: [
                {
                    text: keyEvent.keyOnboardingEvent,
                },
            ],
            spacing: {
                before: 0,
                after: 0,
            },
        }),
        createTableParagraph({
            text: [
                {
                    text: `${metricTrendSymbol(sentiment)}${
                        keyEvent.timeToCompleteMetrics.timeToValuePercentComparison
                    }%`,
                    bold: true,
                    ...colorMetric(sentiment),
                },
            ],
            spacing: {
                before: 0,
                after: 0,
            },
        }),
        createTableParagraph({
            text: [
                {
                    text: keyEvent.timeToCompleteMetrics.timeToValueSecondComparisonRange,
                },
            ],
            spacing: {
                before: 0,
                after: 0,
            },
        }),
        createTableParagraph({
            text: [
                {
                    text: keyEvent.timeToCompleteMetrics.timeToValueFirstComparisonRange,
                },
            ],
            spacing: {
                before: 0,
                after: 0,
            },
        }),
    ];
}

function formatOnboardingRetentionTable (keyEvent: EventMetrics): Paragraph[] {
    const { retentionMetrics } = keyEvent;
    const { sentiment } = retentionMetrics;

    const colorMetric = (sentiment?: string) => {
        if (sentiment === 'neutral') {
            return {};
        }

        return sentiment === 'positive'
            ? { color: impactBriefTheme.colors.ascendingMetric }
            : { color: impactBriefTheme.colors.descendingMetric };
    };
    const metricTrendSymbol = (sentiment?: string) => {
        return sentiment === 'positive' ? '+' : '';
    };

    return [
        createTableParagraph({
            text: [
                {
                    text: keyEvent.keyOnboardingEvent,
                },
            ],
            spacing: {
                before: 0,
                after: 0,
            },
        }),
        createTableParagraph({
            text: [
                {
                    text: `${metricTrendSymbol(sentiment)}${
                        keyEvent.retentionMetrics.onboardingRetentionPercentDifference
                    }%`,
                    bold: true,
                    ...colorMetric(sentiment),
                },
            ],
            spacing: {
                before: 0,
                after: 0,
            },
        }),
        createTableParagraph({
            text: [
                {
                    text: `${keyEvent.retentionMetrics.completedOnboardingRetentionPercent}%`,
                },
            ],
            spacing: {
                before: 0,
                after: 0,
            },
        }),
        createTableParagraph({
            text: [
                {
                    text: `${keyEvent.retentionMetrics.incompleteOnboardingRetentionPercent}%`,
                },
            ],
            spacing: {
                before: 0,
                after: 0,
            },
        }),
    ];
}

function sortOnboardingMetricsEntries (
    eventA: EventMetrics,
    eventB: EventMetrics,
    metricKey: 'timeToCompleteMetrics' | 'retentionMetrics' | 'onboardingMetrics',
    sortKey: 'timeToValuePercentComparison' | 'onboardingPercentChange' | 'onboardingRetentionPercentDifference',
): number {
    let valueA: number | undefined;
    let valueB: number | undefined;
    let valueASentiment: string | undefined;
    let valueBSentiment: string | undefined;

    // This is to avoid TS errors, if there's a cleaner way to do this, please change it
    if (metricKey === 'timeToCompleteMetrics' && sortKey === 'timeToValuePercentComparison') {
        valueA = (eventA.timeToCompleteMetrics as TimeToCompleteMetrics).timeToValuePercentComparison;
        valueB = (eventB.timeToCompleteMetrics as TimeToCompleteMetrics).timeToValuePercentComparison;
        valueASentiment = (eventA.timeToCompleteMetrics as TimeToCompleteMetrics).sentiment;
        valueBSentiment = (eventB.timeToCompleteMetrics as TimeToCompleteMetrics).sentiment;
    } else if (metricKey === 'onboardingMetrics' && sortKey === 'onboardingPercentChange') {
        valueA = (eventA.onboardingMetrics as OnboardingMetrics).onboardingPercentChange;
        valueB = (eventB.onboardingMetrics as OnboardingMetrics).onboardingPercentChange;
        valueASentiment = (eventA.onboardingMetrics as OnboardingMetrics).sentiment;
        valueBSentiment = (eventB.onboardingMetrics as OnboardingMetrics).sentiment;
    } else if (metricKey === 'retentionMetrics' && sortKey === 'onboardingRetentionPercentDifference') {
        valueA = (eventA.retentionMetrics as RetentionMetrics).onboardingRetentionPercentDifference;
        valueB = (eventB.retentionMetrics as RetentionMetrics).onboardingRetentionPercentDifference;
        valueASentiment = (eventA.retentionMetrics as RetentionMetrics).sentiment;
        valueBSentiment = (eventB.retentionMetrics as RetentionMetrics).sentiment;
    }

    if (valueA === undefined || valueB === undefined) {
        return 0;
    }

    if (valueA === valueB) {
        return 0;
    }

    if (valueASentiment === 'positive' && valueBSentiment === 'negative') {
        return -1;
    }
    if (valueASentiment === 'negative' && valueBSentiment === 'positive') {
        return 1;
    }

    return valueA > valueB ? -1 : 1;
}

function insightsBlurb (insight: Template, sentiment: 'positive' | 'negative' | 'neutral' | undefined): Template[] {
    if (sentiment !== 'positive') {
        return [
            {
                function: createParagraph,
                args: {
                    text: [{ text: ' ', textSize: 2 }],
                },
            },
        ];
    }

    return [insight];
}
